import * as THREE from 'three';
import { DETECTION } from '../config/tuning';
import { decodeTerrain, type TerrainHeader } from './TerrainCodec';

/**
 * The heightfield, and the two queries the rest of the game asks of the world (SPEC §3.9.1).
 *
 * `sampleHeight` and `lineOfSight` are the functions M5's detection model is built on, so they
 * are defined and tested here rather than grown later: radar horizon, terrain masking, and the
 * whole business of hiding in a valley all reduce to those two calls.
 *
 * No mesh collider exists anywhere in this project. Terrain collision is a bilinear sample of
 * this grid — O(1), exact, and immune to the tunnelling a mesh collider suffers at 90 m/s
 * (SPEC §4.4).
 *
 * World coordinates: origin at the theatre centre, +x east, −z north, y up. Metres throughout.
 */

/** The minimal contract the flight model needs for ground collision. */
export interface GroundSampler {
  heightAt(xM: number, zM: number): number;
}

const marchPoint = new THREE.Vector3();

export class Terrain {
  readonly resolution: number;
  readonly worldSizeM: number;
  /** Heights in metres, row-major: index = row(z) · resolution + column(x). */
  readonly heights: Float32Array;

  readonly minHeightM: number;
  readonly maxHeightM: number;

  /** Metres between adjacent samples. */
  readonly sampleSpacingM: number;

  /** Sea level, metres. Everything below is bathymetry. */
  readonly seaLevelM = 0;

  private readonly halfWorldM: number;

  constructor(header: TerrainHeader, heights: Float32Array) {
    this.resolution = header.resolution;
    this.worldSizeM = header.worldSizeM;
    this.heights = heights;
    this.halfWorldM = header.worldSizeM * 0.5;
    this.sampleSpacingM = header.worldSizeM / (header.resolution - 1);

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < heights.length; i += 1) {
      if (heights[i] < min) min = heights[i];
      if (heights[i] > max) max = heights[i];
    }
    this.minHeightM = min;
    this.maxHeightM = max;
  }

  /**
   * Terrain height at a world position, metres, bilinearly interpolated.
   *
   * Outside the theatre the edge sample is extended rather than the query failing: a missile that
   * flies past the border still needs a number, and the border of the generated theatre is open
   * sea, so extending it is both cheap and correct.
   */
  sampleHeight(xM: number, zM: number): number {
    const last = this.resolution - 1;
    const gridX = ((xM + this.halfWorldM) / this.worldSizeM) * last;
    const gridZ = ((zM + this.halfWorldM) / this.worldSizeM) * last;

    const x0 = clampIndex(Math.floor(gridX), last);
    const z0 = clampIndex(Math.floor(gridZ), last);
    const x1 = clampIndex(x0 + 1, last);
    const z1 = clampIndex(z0 + 1, last);

    const fx = clamp01(gridX - x0);
    const fz = clamp01(gridZ - z0);

    const row0 = z0 * this.resolution;
    const row1 = z1 * this.resolution;
    const h00 = this.heights[row0 + x0];
    const h10 = this.heights[row0 + x1];
    const h01 = this.heights[row1 + x0];
    const h11 = this.heights[row1 + x1];

    const top = h00 + (h10 - h00) * fx;
    const bottom = h01 + (h11 - h01) * fx;
    return top + (bottom - top) * fz;
  }

  /** GroundSampler: the raw heightfield, seabed included. */
  heightAt(xM: number, zM: number): number {
    return this.sampleHeight(xM, zM);
  }

  /**
   * The higher of terrain and sea level — the surface an aircraft would hit and the one a radar
   * altimeter reads. Over the shelf the seabed is 190 m below the water, and reporting height
   * above *that* would tell a pilot at 30 m over the sea that they had 220 m in hand.
   */
  surfaceHeightAt(xM: number, zM: number): number {
    const height = this.sampleHeight(xM, zM);
    return height > this.seaLevelM ? height : this.seaLevelM;
  }

  /** Height of a point above the surface beneath it, metres. Negative means below it. */
  altitudeAglM(positionM: THREE.Vector3): number {
    return positionM.y - this.surfaceHeightAt(positionM.x, positionM.z);
  }

  /** Surface normal at a world position, written into `out`. */
  sampleNormal(xM: number, zM: number, out: THREE.Vector3): THREE.Vector3 {
    const step = this.sampleSpacingM;
    const dx = this.sampleHeight(xM + step, zM) - this.sampleHeight(xM - step, zM);
    const dz = this.sampleHeight(xM, zM + step) - this.sampleHeight(xM, zM - step);
    return out.set(-dx, 2 * step, -dz).normalize();
  }

  /** Slope at a world position, radians from horizontal. */
  sampleSlopeRad(xM: number, zM: number): number {
    const step = this.sampleSpacingM;
    const dx = (this.sampleHeight(xM + step, zM) - this.sampleHeight(xM - step, zM)) / (2 * step);
    const dz = (this.sampleHeight(xM, zM + step) - this.sampleHeight(xM, zM - step)) / (2 * step);
    return Math.atan(Math.sqrt(dx * dx + dz * dz));
  }

  /**
   * True when the straight line from `a` to `b` clears the terrain.
   *
   * A fixed-step march rather than a DDA: the step count bounds the cost regardless of range,
   * which is what lets M5 run this for every site against every target at 10 Hz. The trade is
   * that a knife-edge ridge thinner than one step can be missed; at 64 steps over a 20 km
   * engagement that is a 300 m sample interval, which is comfortably finer than any ridge that
   * would actually mask an aircraft.
   *
   * Endpoints are excluded. A radar sits *on* the ground, and including its own sample would
   * report every site as blind.
   */
  lineOfSight(a: THREE.Vector3, b: THREE.Vector3, steps: number = DETECTION.losSteps): boolean {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;

    for (let i = 1; i < steps; i += 1) {
      const t = i / steps;
      const x = a.x + dx * t;
      const z = a.z + dz * t;
      const rayY = a.y + dy * t;
      if (this.sampleHeight(x, z) > rayY + DETECTION.losClearanceM) return false;
    }
    return true;
  }

  /**
   * Distance along a→b at which terrain first blocks the line, or −1 when it is clear.
   * Used by the M4 haze and M5 debug visualisation; shares the march above.
   */
  firstOcclusionM(a: THREE.Vector3, b: THREE.Vector3, steps: number = DETECTION.losSteps): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

    for (let i = 1; i < steps; i += 1) {
      const t = i / steps;
      marchPoint.set(a.x + dx * t, a.y + dy * t, a.z + dz * t);
      if (this.sampleHeight(marchPoint.x, marchPoint.z) > marchPoint.y + DETECTION.losClearanceM) {
        return length * t;
      }
    }
    return -1;
  }

  /**
   * A min/max pyramid over the grid, coarsest level last. The terrain renderer culls and picks
   * LOD against these bounds; without them every quadtree node would have to assume the full
   * height range and the frustum test would reject almost nothing.
   */
  buildBoundsPyramid(levels: number): TerrainBounds[] {
    const pyramid: TerrainBounds[] = [];
    let nodesPerSide = 1 << levels;

    for (let level = 0; level <= levels; level += 1) {
      const min = new Float32Array(nodesPerSide * nodesPerSide);
      const max = new Float32Array(nodesPerSide * nodesPerSide);

      if (level === 0) {
        // Finest level: scan the samples each node covers.
        const samplesPerNode = Math.ceil((this.resolution - 1) / nodesPerSide);
        for (let nz = 0; nz < nodesPerSide; nz += 1) {
          for (let nx = 0; nx < nodesPerSide; nx += 1) {
            let nodeMin = Number.POSITIVE_INFINITY;
            let nodeMax = Number.NEGATIVE_INFINITY;
            const z0 = nz * samplesPerNode;
            const x0 = nx * samplesPerNode;
            const z1 = Math.min(z0 + samplesPerNode, this.resolution - 1);
            const x1 = Math.min(x0 + samplesPerNode, this.resolution - 1);
            for (let z = z0; z <= z1; z += 1) {
              const row = z * this.resolution;
              for (let x = x0; x <= x1; x += 1) {
                const height = this.heights[row + x];
                if (height < nodeMin) nodeMin = height;
                if (height > nodeMax) nodeMax = height;
              }
            }
            min[nz * nodesPerSide + nx] = nodeMin;
            max[nz * nodesPerSide + nx] = nodeMax;
          }
        }
      } else {
        // Coarser levels: combine the four children below.
        const finer = pyramid[level - 1];
        const finerSide = finer.nodesPerSide;
        for (let nz = 0; nz < nodesPerSide; nz += 1) {
          for (let nx = 0; nx < nodesPerSide; nx += 1) {
            let nodeMin = Number.POSITIVE_INFINITY;
            let nodeMax = Number.NEGATIVE_INFINITY;
            for (let cz = 0; cz < 2; cz += 1) {
              for (let cx = 0; cx < 2; cx += 1) {
                const index = (nz * 2 + cz) * finerSide + (nx * 2 + cx);
                if (finer.min[index] < nodeMin) nodeMin = finer.min[index];
                if (finer.max[index] > nodeMax) nodeMax = finer.max[index];
              }
            }
            min[nz * nodesPerSide + nx] = nodeMin;
            max[nz * nodesPerSide + nx] = nodeMax;
          }
        }
      }

      pyramid.push({ nodesPerSide, min, max });
      nodesPerSide >>= 1;
    }

    return pyramid;
  }
}

export interface TerrainBounds {
  nodesPerSide: number;
  min: Float32Array;
  max: Float32Array;
}

function clampIndex(value: number, last: number): number {
  return value < 0 ? 0 : value > last ? last : value;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Gzip's magic bytes. */
const GZIP_MAGIC_0 = 0x1f;
const GZIP_MAGIC_1 = 0x8b;

/**
 * Fetch and decode the committed heightfield.
 *
 * The file on disk is a gzip stream, but whether it arrives *as* one depends on the server: Vite's
 * dev server — and many static hosts — recognise the content and serve it with
 * `Content-Encoding: gzip`, so the browser has already decompressed it by the time it reaches
 * here. Sniffing the magic bytes and decompressing only when needed makes the loader work under
 * either behaviour, rather than working in development and failing on deploy.
 *
 * `DecompressionStream` is used rather than a bundled inflate: native in every browser the
 * project targets, and no bundle cost.
 */
export async function loadTerrain(url: string): Promise<Terrain> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`loadTerrain: ${url} responded ${response.status}`);
  }

  const raw = new Uint8Array(await response.arrayBuffer());
  const stillCompressed = raw.length > 2 && raw[0] === GZIP_MAGIC_0 && raw[1] === GZIP_MAGIC_1;
  const payload = stillCompressed ? await gunzip(raw) : raw;

  const { header, heights } = decodeTerrain(payload);
  return new Terrain(header, heights);
}

async function gunzip(compressed: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([compressed as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
